<template>
  <div v-if="showComponent">
    <label
      v-if="label"
      :title="label"
      class="text-truncate"
    >
      {{ label }} <small v-if="isRequired">*</small>
    </label>
    <font-awesome-icon
      icon="question-circle"
      size="sm"
      class="clickable float-right mt-1 ml-2"
      v-if="help"
      v-b-tooltip.hover="help"
    />
    <font-awesome-icon
      id="font-awesome-icon-edit"
      icon="edit"
      size="sm"
      class="clickable float-right mt-1"
      v-if="showEditButton"
      @click="$emit('edit-button-clicked')"
    />
    <div>
      <prism-editor
        class="my-prism-editor shadow rounded"
        language="html"
        :highlight="highlighter"
        :line-numbers="lineNumbers"
        :value="data"
        :style="styles"
        @input="$emit('input', $event || null)"
        @keydown="onKeyDown"
      ></prism-editor>
    </div>
    <b-row class="mt-2">
      <LmButton
        class="col-auto"
        v-if="btnPreview"
        type="button"
        :value="$t('show_preview')"
        size="sm"
        @click="onShowPreview"
      />
      <LmButton
        class="col-auto"
        v-if="variables.length"
        buttonClass="btn-link shadow-none"
        size="sm"
        type="button"
        :value="$t('show_variables')"
        @click="$refs.variablesTable.show()"
      />
    </b-row>
    <b-modal
      :title="$t('variabls')"
      ref="variablesTable"
      size="lg"
    >
      <b-table striped hover :items="variables" :fields="fields">
        <template #cell(actions)="row">
          <font-awesome-icon
            icon="clipboard"
            @click="onCopyVariable(row.item)"
            class="text-primary clickable"
            style="font-size: 1.5em"
            v-b-tooltip.hover.top="$t('copy_to_clipboard')"
          ></font-awesome-icon>
      </template>
      </b-table>
    </b-modal>
    <LmTemplatePreviewModal ref="lmTemplatePreviewModal"/>
  </div>
</template>

<script>
// Plugin
import { PrismEditor } from 'vue-prism-editor'
import 'vue-prism-editor/dist/prismeditor.min.css'
import { highlight, languages } from 'prismjs/components/prism-core'
import 'prismjs/components/prism-clike'
import 'prismjs/components/prism-markup'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-css'
import 'prismjs/themes/prism-okaidia.css'

// Utils
import LmCommonProps from '@/mixins/LmCommonProps'
import lmToasts from '@/mixins/lmToasts.js'
import commonsApi from '@/api/commons'
import LmTemplatePreviewModal from '@/components/modals/LmTemplatePreviewModal'

// Components
import LmButton from '@/components/form/elements/LmButton'

export default {
  mixins: [LmCommonProps, lmToasts],
  props: {
    btnPreview: {
      type: Boolean,
      default: false
    },
    variables: {
      type: Array,
      default: () => []
    },
    pathToPreview: {
      type: String,
      default: null
    },
    defaultTemplate: {
      type: String,
      default: null
    },
    maxHeight: {
      type: String,
      default: '450px'
    }
  },
  components: {
    PrismEditor,
    LmTemplatePreviewModal,
    LmButton
  },
  computed: {
    data () {
      return this.value || null
    },
    styles () {
      return `max-height: ${this.maxHeight}`
    }
  },
  data () {
    return {
      lineNumbers: true,
      fields: [
        { label: this.$t('description'), key: 'text' },
        { label: this.$t('variable'), key: 'value' },
        { label: this.$t('type'), key: 'type' },
        { label: this.$t('actions'), key: 'actions' }
      ]
    }
  },
  methods: {
    async onCopyVariable (e) {
      try {
        let textToCopy = ''
        if (e.type.toLowerCase() === 'array') {
          textToCopy = '{{ #each ' + e.value + ' }}\n  {{ this }}\n{{ /each }}'
        } else if (e.type.toLowerCase() === 'boolean') {
          textToCopy = '{{ #if ' + e.value + ' }}\n  ...\n{{ /if }}'
        } else if (e.type.toLowerCase() === 'variable') {
          textToCopy = `{{ ${e.value} }}`
        } else {
          textToCopy = e.value
        }
        await navigator.clipboard.writeText(textToCopy.trim())
        this.lmToastSuccessMessage(`Se copió la variable "${e.value}" al porta papeles.`, '¡Copiado!', 3000)
      } catch (error) {
        console.log(error)
      }
    },
    highlighter (data) {
      return highlight(data, {
        ...languages['markup'],
        ...languages['js'],
        ...languages['css']
      },
      'markup'
      )
    },
    async onShowPreview () {
      try {
        if (!this.pathToPreview) throw new Error('Path para el preview no especificado.')

        const response = await commonsApi.axiosPostWrapper(this.pathToPreview, {
          template: this.data.trim(),
          templateCtx: this.variables
        })
        const template = JSON.parse(response.data.template)?.html || JSON.parse(response.data.template)
        this.$refs.lmTemplatePreviewModal.openEmailPreviewModal(template)
      } catch (error) {
        console.log(error)
      }
    },
    onKeyDown (event) {
      // Nota: en modo edición, se inserta el codigo en el componente
      // Si apretamos inmediatamente ctrl + z volvemos al componente vacío
      if (event.keyCode === 90 && event.ctrlKey) {
        event.preventDefault()
      }
    }
  }
}
</script>

<style lang="sass">
.prism-editor__editor
  color: #fafafa

.my-prism-editor
  background: #363636
  font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace
  font-size: 1rem
  line-height: 1.6
  padding: 10px

.prism-editor__textarea:focus
  outline: none
</style>
